import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, message, InputNumber, Checkbox, Button, Card } from "antd";
import { all_routes } from "../../../router/all_routes";
import { addTeacher, updateTeacher, getTeacher, getAssignableSubjects } from "../../../services/teacherService";
import CommonSelect from "../../Common/CommonSelect";
import CommonMultiSelect from "../../Common/CommonMultiSelect";
import CommonFormLayout from "../../Common/CommonFormLayout";
import { parseSQLDate, formatToSQLDate } from "../../../utils/dateUtils";
import { GENDER_OPTIONS, CITIES_OPTIONS } from "../../../constants/userConstants";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { isValidCIN, isValidPhoneNumber } from "utils/validators";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { CLASS_SECTION_OPTIONS_BY_LEVEL } from "constants/classConstants";
import { CONTRACT_TYPE_OPTIONS } from "constants/staffConstants";
import { useAbility } from "config/abilityContext";
import useReference from "hooks/useReference";
import useUsers from "feature-module/superAdmin/hooks/useUsers";
import EmailSearchComponent from "components/Common/EmailSearchComponent";

const AssignmentRow: React.FC<{
  name: number;
  remove: (name: number) => void;
  subjects: { value: string; label: string }[];
  subjectClassMap: Record<string, { value: string; label: string; schoolCycle?: string }[]>;
  subjectsByCycle: any;
  form: any;
  t: any;
}> = ({ name, remove, subjects, subjectClassMap, subjectsByCycle, form, t }) => {
  const [section, setSection] = React.useState<string | undefined>(undefined);

  const selectedSubject = Form.useWatch(["assignments", name, "subjectId"], form);
  const subjectId = selectedSubject?.value || selectedSubject;

  React.useEffect(() => {
    setSection(section ?? subjectClassMap[subjectId]?.[0]?.schoolCycle);
    form.setFieldsValue({
      assignments: {
        [name]: { subjectId },
      },
    });
  }, [selectedSubject]);

  const handleSectionChange = (value: string) => {
    setSection(value);
    const assignments = form.getFieldValue("assignments") || [];
    assignments[name] = {
      ...assignments[name],
      subjectId: undefined,
      classIds: [],
    };
    form.setFieldsValue({ assignments });
  };

  const subjectOptions = useMemo(() => {
    return (subjectsByCycle[section ?? ""] || []).map((subject: any) => ({
      value: subject.id,
      label: getSubjectLabelFromCode(subject.code, t),
    }));
  }, [section, t]);

  const classOptions = useMemo(() => {
    return (subjectClassMap[subjectId] || []).filter(
      (c: any) => !section || c?.schoolCycle === section
    );
  }, [subjectId, section]);

  return (
    <div className="row g-3 align-items-start mb-3">
      <div className="col-md-3">
        <Form.Item label={t("section")} className="mb-0" >
          <CommonSelectLite
            options={CLASS_SECTION_OPTIONS_BY_LEVEL(t)}
            optionValueKey="value"
            optionLabelKey="label"
            value={section}
            onChange={handleSectionChange}
          />
        </Form.Item>
      </div>

      <div className="col-md-3">
        <Form.Item
          name={[name, "subjectId"]}
          label={t("assign_subject")}
          rules={[{ required: true, message: t("subject_required") }]}
          className="mb-0"
        >
          <CommonSelectLite
            options={subjectOptions}
            onChange={(option) => {
              const assignments = form.getFieldValue("assignments") || [];
              assignments[name] = {
                ...assignments[name],
                subjectId: option,
                classIds: [],
              };
              form.setFieldsValue({ assignments });
            }}
            value={subjectId}
            optionValueKey="value"
            optionLabelKey="label"
            disabled={!section}
          />
        </Form.Item>
      </div>

      <div className="col-md-5">
        <Form.Item
          name={[name, "classIds"]}
          label={t("assign_classes")}
          rules={[{ required: true, message: t("class_required") }]}
          className="mb-0"
        >
          <CommonMultiSelect
            options={classOptions}
            placeholder={t("select_classes")}
            disabled={!section || !subjectId}
          />
        </Form.Item>
      </div>

      <div className="col-md-1">
        <Form.Item label=" " colon={false} className="mb-0 d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => remove(name)}
          >
            <i className="ti ti-trash" />
          </button>
        </Form.Item>
      </div>
    </div>
  );
};

const TeacherForm = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const { id: teacherId } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [subjects, setSubjects] = useState<{ value: string; label: string }[]>([]);
  const [subjectClassMap, setSubjectClassMap] = useState<Record<string, { value: string; label: string }[]>>({});
  const navigate = useNavigate();
  const [addInfoStaff, setAddInfoStaff] = React.useState<boolean>(false);
  const ability = useAbility();
  const havePermissionStaff = isEdit ? ability.can("update", "Staff") : ability.can("create", "Staff");
  const contract = Form.useWatch(['staff', 'contract'], form) ?? null;
  const translatedGender = GENDER_OPTIONS.map((gender) => ({
    value: gender.value,
    label: t(gender.label),
  }));
  const { jobPositions, fetchJobPositions } = useReference()
  const [staffIsExistInEdit, setStaffIsExistInEdit] = useState<boolean>(false);
  const [subjectsByCycle, setSubjectsByCycle] = useState<any>({});
  const [isSearchedEmail, setIsSearchedEmail] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const { handleFindOneByEmailAndRole } = useUsers();

  useEffect(() => {
    fetchSubjects();
    fetchJobPositions();
    if (teacherId) {
      setIsEdit(true);
      setShowForm(true); // Show form directly in edit mode
      fetchTeacherData(teacherId);
    }
  }, [teacherId]);

  const jobPositionOptions = useMemo(() => {
    return jobPositions.map((jobPosition: any) => ({
      value: jobPosition.id,
      label: t("job_positions." + jobPosition.code),
    }));
  }, [jobPositions]);

  const fetchSubjects = async () => {
    try {
      const response = await getAssignableSubjects();
      setSubjects(response.data.subjects.map((subject: any) => ({
        value: subject.id,
        label: getSubjectLabelFromCode(subject?.code, t)
      })));
      setSubjectsByCycle(response.data.subjectsByCycle);

      const classMapData = response.data.classMap as Record<string, { id: string; name: string; schoolCycle: string }[]>;
      const transformedMap: Record<string, { value: string; label: string; schoolCycle: string }[]> = {};
      for (const [subjectId, classList] of Object.entries(classMapData)) {
        transformedMap[subjectId] = classList.map((c) => ({
          value: c.id,
          label: c.name,
          schoolCycle: c.schoolCycle
        }));
      }
      setSubjectClassMap(transformedMap);
    } catch (error) {
      message.error(t("failed_to_load_subjects"));
    }
  };

  const fetchTeacherData = async (id: string) => {
    try {
      const teacherResponse = await getTeacher(id);
      const teacherData = teacherResponse.data;
      if (subjects.length === 0) await fetchSubjects();

      const initialData = {
        user: {
          firstName: teacherData.user?.firstName || "",
          lastName: teacherData.user?.lastName || "",
          email: teacherData.user?.email || "",
          phoneNumber: teacherData.user?.phoneNumber || "",
          birthDate: parseSQLDate(teacherData.user?.birthDate),
          gender: teacherData.user?.gender || null,
          cityOfBirth: CITIES_OPTIONS.find((city) => city.name === teacherData.user?.cityOfBirth)?.name || "",
          zipCode: teacherData.user?.zipCode || "",
          address: teacherData.user?.address || "",
          city: CITIES_OPTIONS.find((city) => city.name === teacherData.user?.city)?.name || "",
        },
        staff: teacherData.staff ? {
          contract: teacherData.staff?.contract || "",
          startDate: parseSQLDate(teacherData.staff?.startDate),
          endDate: parseSQLDate(teacherData.staff?.endDate),
          monthlySalary: teacherData.staff?.monthlySalary || 0,
          cinNumber: teacherData.staff?.cinNumber || null,
          cnssNumber: teacherData.staff?.cnssNumber || null,
          matricule: teacherData.staff?.matricule || null,
          jobPositionId: teacherData.staff?.jobPositionId || null,
        } : null,
        assignments: teacherData.assignments.map((a: any) => ({
          subjectId:
            subjects.find((s) => s.value === a.subjectId) || {
              value: a.subjectId,
              label: a.subjectName,
            },
          classIds: a.classIds,
        })),
      };

      setStaffIsExistInEdit(initialData?.staff !== null);
      form.setFieldsValue(initialData);
      setAddInfoStaff(initialData?.staff !== null);
    } catch (error) {
      message.error(t("failed_to_load_teacher_data"));
    }
  };

  const handleEmailFound = (infoUser: any) => {
    const teacherData = {
      user: {
        userId: infoUser?.id,
        firstName: infoUser?.firstName || "",
        lastName: infoUser?.lastName || "",
        email: infoUser?.email || "",
        phoneNumber: infoUser?.phoneNumber || "",
        birthDate: parseSQLDate(infoUser?.birthDate),
        gender: infoUser?.gender || null,
        cityOfBirth: CITIES_OPTIONS.find((city) => city.name === infoUser?.cityOfBirth)?.name || "",
        zipCode: infoUser?.zipCode || "",
        address: infoUser?.address || "",
        city: CITIES_OPTIONS.find((city) => city.name === infoUser?.city)?.name || "",
      },
      staff: infoUser.staff ? {
        contract: infoUser.staff?.contract || "",
        startDate: parseSQLDate(infoUser.staff?.startDate),
        endDate: parseSQLDate(infoUser.staff?.endDate),
        monthlySalary: infoUser.staff?.monthlySalary || 0,
        cinNumber: infoUser.staff?.cinNumber || null,
        cnssNumber: infoUser.staff?.cnssNumber || null,
        matricule: infoUser.staff?.matricule || null,
        jobPositionId: infoUser.staff?.jobPositionId || null,
      } : null,
      assignments: [],
    };

    form.setFieldsValue(teacherData);
    setAddInfoStaff(teacherData?.staff !== null);
    setIsSearchedEmail(true);
    setShowForm(true);
  };

  const handleEmailNotFound = (email: string) => {
    form.resetFields();
    form.setFieldsValue({
      user: {
        email: email
      }
    });
    setIsSearchedEmail(true);
    setShowForm(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const rawAssignments = Array.isArray(values.assignments)
        ? values.assignments
        : [];

      const teacherData = {
        ...values,
        user: {
          ...values.user,
          birthDate: values.user.birthDate
            ? formatToSQLDate(values.user.birthDate)
            : null,
        },
        staff: values?.staff ? {
          ...values.staff,
          startDate: values?.staff?.startDate
            ? formatToSQLDate(values?.staff?.startDate)
            : null,
          endDate: values?.staff?.endDate
            ? formatToSQLDate(values?.staff?.endDate)
            : null,
        } : null,
        assignments: rawAssignments.map((item: any) => ({
          subjectId: item.subjectId?.value || item.subjectId,
          classIds: item.classIds,
        })),
      };

      if (isEdit) {
        await updateTeacher(teacherId!, teacherData);
        message.success(t("teacher_updated_successfully"));
      } else {
        await addTeacher(teacherData);
        message.success(t("teacher_added_successfully"));
      }

      navigate(routes.teacherList);
    } catch (error: any) {
      message.error(t("failed_to_save_teacher_data"));
    }
  };

  const emailSearchSection = !isEdit && (
    <EmailSearchComponent
      onEmailFound={handleEmailFound}
      onEmailNotFound={handleEmailNotFound}
      searchService={handleFindOneByEmailAndRole}
      role="teacher"
      title={t("teacherPage.search_teacher_by_email")}
      helpText={t("teacherPage.teacher_email_search_help")}
    />
  );

  const sections = [
    {
      title: t("personal_information"),
      content: (
        <div className="row">
          <div className="col-md-4">
            <Form.Item
              name={["user", "userId"]}
              hidden
            >
              <Input type="hidden" />
            </Form.Item>
            <Form.Item
              label={t("first_name")}
              name={["user", "firstName"]}
              rules={[{ required: true, message: t("validation.firstName_required") }]}
            >
              <Input placeholder={t("enter_first_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("last_name")}
              name={["user", "lastName"]}
              rules={[{ required: true, message: t("validation.lastName_required") }]}
            >
              <Input placeholder={t("enter_last_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("gender")}
              name={["user", "gender"]}
              rules={[{ required: true, message: t("gender_required") }]}
            >
              <CommonSelect
                options={translatedGender}
                defaultValue={translatedGender.find(
                  (opt) => opt.value === form.getFieldValue(["user", "gender"])
                )}
                onChange={(option) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue().user,
                      gender: option?.value ?? "",
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("email_address")}
              name={["user", "email"]}
              rules={[
                { required: true, message: t("email_required") },
                { type: "email", message: t("validation.please_enter_valid_email") },
              ]}
            >
              <Input
                placeholder={t("enter_email")}
                disabled={isSearchedEmail} // Disable email after search to prevent changes
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("phone_number")}
              name={["user", "phoneNumber"]}
              rules={[
                { required: true, message: t("validation.phoneNumber_required") },
                {
                  validator: (_, value) =>
                    value && !isValidPhoneNumber(value)
                      ? Promise.reject(t("validation.phone_number_invalid"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input placeholder={t("enter_phone_number")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("birth_date")}
              name={["user", "birthDate"]}
              rules={[{ required: true, message: t("validation.birthdate_required") }]}
            >
              <LocalizedDatePicker className="form-control datetimepicker" />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city_of_birth")}
              name={["user", "cityOfBirth"]}
              rules={[{ required: true, message: t("validation.cityOfBirth_required") }]}
            >
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                value={form.getFieldValue(["user", "cityOfBirth"]) || "casablanca"}
                placeholder={t("enter_city_of_birth")}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue().user,
                      cityOfBirth: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("address")}
              name={["user", "address"]}
              rules={[{ required: true, message: t("validation.address_required") }]}
            >
              <Input placeholder={t("enter_address")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("zip_code")}
              name={["user", "zipCode"]}
              rules={[{ required: true, message: t("validation.zipCode_required") }]}
            >
              <Input placeholder={t("enter_zip_code")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city")}
              name={["user", "city"]}
              rules={[{ required: true, message: t("validation.city_required") }]}
            >
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                placeholder={t("enter_city")}
                value={form.getFieldValue(["user", "city"])}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue().user,
                      city: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      title: t("staff.info_contract"),
      hidden: !havePermissionStaff,
      content: (
        <>
          {(!isEdit || !staffIsExistInEdit) && (
            <div className="row mb-2">
              <Checkbox
                checked={addInfoStaff}
                onChange={(e) =>
                  setAddInfoStaff(e.target.checked)
                }
              >
                {t("staff.add_info_staff")}
              </Checkbox>
            </div>
          )}

          {addInfoStaff && (
            <div className="row">
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.contractType")}
                  name={["staff", "contract"]}
                  rules={[{ required: true, message: t("staff.validation.contractType_required") }]}
                >
                  <CommonSelectLite
                    options={CONTRACT_TYPE_OPTIONS}
                    optionValueKey="value"
                    optionLabelKey="label"
                    placeholder={t("staff.form.contractType")}
                    value={form.getFieldValue("staff.contract")}
                    onChange={(value) =>
                      form.setFieldsValue({
                        staff: {
                          ...form.getFieldsValue().staff,
                          contract: value,
                        },
                      })
                    }
                  />
                </Form.Item>
              </div>

              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.startDate")}
                  name={["staff", "startDate"]}
                  rules={[{ required: true, message: t("staff.validation.startDate_required") }]}
                >
                  <LocalizedDatePicker className="form-control datetimepicker" />
                </Form.Item>
              </div>
              {contract !== "CDI" && (
                <div className="col-md-4">
                  <Form.Item
                    label={t("staff.form.endDate")}
                    name={["staff", "endDate"]}
                    rules={[{ required: true, message: t("staff.validation.endDate_required") }]}
                  >
                    <LocalizedDatePicker className="form-control datetimepicker" />
                  </Form.Item>
                </div>
              )}
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.monthlySalary")}
                  name={["staff", "monthlySalary"]}
                  rules={[{ required: true, message: t("staff.validation.monthlySalary_required") }]}
                >
                  <InputNumber
                    placeholder={t("staff.placeholders.enter_monthly_salary")}
                    min={0}
                    className="d-flex"
                    precision={2}
                    addonAfter="MAD"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.cinNumber")}
                  name={["staff", "cinNumber"]}
                  rules={[{ required: true, message: t("staff.validation.cinNumber_required") }, {
                    validator: (_, value) =>
                      value && !isValidCIN(value)
                        ? Promise.reject(t("staff.validation.cinNumber_invalid"))
                        : Promise.resolve(),
                  }]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_cinNumber")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.cnssNumber")}
                  name={["staff", "cnssNumber"]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_cnssNumber")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.matricule")}
                  name={["staff", "matricule"]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_matricule")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.jobPosition")}
                  name={["staff", "jobPositionId"]}
                  rules={[{ required: true, message: t("staff.validation.jobPosition_required") }]}
                >
                  <CommonSelect
                    options={jobPositionOptions}
                    defaultValue={jobPositionOptions.find(
                      (opt) => opt.value === form.getFieldValue(["staff", "jobPositionId"])
                    )}
                    onChange={(option) =>
                      form.setFieldsValue({
                        staff: {
                          ...form.getFieldsValue(),
                          jobPositionId: option?.value ?? "",
                        },
                      })
                    }
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: t("assignments"),
      content: (
        <>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.assignments !== curr.assignments}
          >
            {({ getFieldValue }) => {
              const currentAssignments = getFieldValue("assignments") || [];
              return (
                <Form.List name="assignments">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }) => (
                        <AssignmentRow
                          key={key}
                          name={name}
                          remove={remove}
                          subjects={subjects}
                          subjectClassMap={subjectClassMap}
                          subjectsByCycle={subjectsByCycle}
                          form={form}
                          t={t}
                        />
                      ))}
                      <Form.Item>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => add()}
                        >
                          <i className="ti ti-plus me-1" />
                          {t("add_assignment")}
                        </button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              );
            }}
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <CommonFormLayout
      title={isEdit ? t("edit_teacher") : t("add_teacher")}
      form={form}
      onFinish={handleSubmit}
      sections={showForm ? sections : []}
      cancelRoute={routes.teacherList}
      submitText={isEdit ? t("update") : t("add")}
      isEdit={isEdit}
      customContentBefore={emailSearchSection}
    />
  );
};

export default TeacherForm;